import { useEffect, useRef } from 'react';

import { CHART_COLORS } from '../styles/colors';
import { isBlockLoaded } from '../components/loading';

const CHART_JS_CDN =
  'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';

function buildChartOptions() {
  const tooltip = {
    backgroundColor: CHART_COLORS.tooltipBg,
    titleColor: CHART_COLORS.tooltipTitle,
    bodyColor: CHART_COLORS.tooltipBody,
    borderColor: CHART_COLORS.tooltipBorder,
    borderWidth: 1,
    cornerRadius: 8,
    padding: 10,
  };

  const basePlugins = { legend: { display: false }, tooltip };
  const baseScales = {
    x: { grid: { color: CHART_COLORS.grid }, ticks: { font: { size: 11 } } },
    y: { grid: { color: CHART_COLORS.grid }, ticks: { font: { size: 11 } } },
  };

  return { tooltip, basePlugins, baseScales };
}

function createChallanDonutChart(el, challanStatus) {
  const { tooltip } = buildChartOptions();

  return new window.Chart(el, {
    type: 'doughnut',
    data: {
      labels: ['Delivered', 'Active Challans', 'Pending', 'Cancel'],
      datasets: [{
        data: challanStatus.donutData,
        backgroundColor: [
          CHART_COLORS.green,
          CHART_COLORS.blue,
          CHART_COLORS.amber,
          CHART_COLORS.red,
        ],
        borderWidth: 2,
        borderColor: CHART_COLORS.chartBorder,
        hoverOffset: 4,
      }],
    },
    options: {
      responsive: false,
      cutout: '68%',
      plugins: { legend: { display: false }, tooltip },
    },
  });
}

function updateChallanDonutChart(chart, challanStatus) {
  chart.data.datasets[0].data = challanStatus.donutData;
  chart.update();
}

function createChallanTrendChart(el, challanStatus) {
  const { basePlugins, baseScales } = buildChartOptions();

  return new window.Chart(el, {
    type: 'bar',
    data: {
      labels: challanStatus.trendLabels,
      datasets: [{
        data: challanStatus.trendData,
        backgroundColor: (ctx) =>
          ctx.dataIndex === challanStatus.trendData.length - 1
            ? CHART_COLORS.violet
            : CHART_COLORS.violetAlpha30,
        borderRadius: 5,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: basePlugins,
      scales: {
        x: { ...baseScales.x, grid: { display: false } },
        y: { ...baseScales.y },
      },
    },
  });
}

function updateChallanTrendChart(chart, challanStatus) {
  chart.data.labels = challanStatus.trendLabels;
  chart.data.datasets[0].data = challanStatus.trendData;
  chart.update();
}

function getActiveDrivers(driverUtilization) {
  return driverUtilization.filter((driver) => driver.assigned > 0);
}

function createDriverChart(el, driverUtilization) {
  const { basePlugins, baseScales } = buildChartOptions();
  const activeDrivers = getActiveDrivers(driverUtilization);

  return new window.Chart(el, {
    type: 'bar',
    data: {
      labels: activeDrivers.map((driver) => driver.name),
      datasets: [
        {
          label: 'Assigned',
          data: activeDrivers.map((driver) => driver.assigned),
          backgroundColor: CHART_COLORS.blueAlpha25,
          borderRadius: 4,
          borderSkipped: false,
        },
        {
          label: 'Delivered',
          data: activeDrivers.map((driver) => driver.delivered),
          backgroundColor: CHART_COLORS.green,
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        ...basePlugins,
        legend: {
          display: true,
          position: 'top',
          labels: {
            boxWidth: 8,
            font: { size: 10 },
            color: CHART_COLORS.chartDefault,
            padding: 12,
            usePointStyle: true,
          },
        },
      },
      scales: {
        x: { ...baseScales.x, grid: { display: false } },
        y: { ...baseScales.y, ticks: { stepSize: 5 } },
      },
    },
  });
}

function updateDriverChart(chart, driverUtilization) {
  const activeDrivers = getActiveDrivers(driverUtilization);
  chart.data.labels = activeDrivers.map((driver) => driver.name);
  chart.data.datasets[0].data = activeDrivers.map((driver) => driver.assigned);
  chart.data.datasets[1].data = activeDrivers.map((driver) => driver.delivered);
  chart.update();
}

function destroyChart(instancesRef, key) {
  const chart = instancesRef.current[key];
  if (chart) {
    chart.destroy();
    instancesRef.current[key] = null;
  }
}

function loadChartJs() {
  return new Promise((resolve) => {
    if (window.Chart) {
      resolve();
      return;
    }

    const existingScript = document.querySelector(`script[src="${CHART_JS_CDN}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = CHART_JS_CDN;
    script.async = true;
    script.onload = resolve;
    document.head.appendChild(script);
  });
}

function applyChartDefaults() {
  if (!window.Chart) return;
  window.Chart.defaults.color = CHART_COLORS.chartDefault;
  window.Chart.defaults.font.family = "'DM Sans', sans-serif";
}

function useManagedChart({
  instancesRef,
  chartKey,
  elementId,
  ready,
  data,
  create,
  update,
}) {
  useEffect(() => {
    if (!ready) {
      destroyChart(instancesRef, chartKey);
      return undefined;
    }

    let isMounted = true;

    const setupChart = async () => {
      await loadChartJs();
      if (!isMounted) return;

      applyChartDefaults();

      const el = document.getElementById(elementId);
      if (!el) return;

      const existing = instancesRef.current[chartKey];
      if (existing) {
        update(existing, data);
        return;
      }

      instancesRef.current[chartKey] = create(el, data);
    };

    setupChart();

    return () => {
      isMounted = false;
    };
  }, [ready, data, chartKey, elementId, create, update, instancesRef]);
}

/**
 * Initializes Chart.js instances once each canvas block is ready.
 * Charts update in place when their section data changes (e.g. date refetch).
 */
export function useCharts(chartData, loadingState) {
  const chartInstancesRef = useRef({});

  const challanStatusReady = isBlockLoaded(loadingState, 'challan', 'status');
  const challanTrendReady = isBlockLoaded(loadingState, 'challan', 'trend');
  const driverProgressReady = isBlockLoaded(loadingState, 'driver', 'progress');

  const { challanStatus, driverUtilization } = chartData;

  useManagedChart({
    instancesRef: chartInstancesRef,
    chartKey: 'challanDonut',
    elementId: 'challanDonut',
    ready: challanStatusReady,
    data: challanStatus,
    create: createChallanDonutChart,
    update: updateChallanDonutChart,
  });

  useManagedChart({
    instancesRef: chartInstancesRef,
    chartKey: 'challanTrend',
    elementId: 'challanTrend',
    ready: challanTrendReady,
    data: challanStatus,
    create: createChallanTrendChart,
    update: updateChallanTrendChart,
  });

  useManagedChart({
    instancesRef: chartInstancesRef,
    chartKey: 'driverChart',
    elementId: 'driverChart',
    ready: driverProgressReady,
    data: driverUtilization,
    create: createDriverChart,
    update: updateDriverChart,
  });

  useEffect(() => () => {
    Object.keys(chartInstancesRef.current).forEach((key) => {
      destroyChart(chartInstancesRef, key);
    });
  }, []);
}
